<template>
  <div class="role-list">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="bi bi-shield-lock me-2"></i>
            Roles
          </h1>
          <p class="page-description">
            Administración de roles y permisos del sistema
          </p>
        </div>
        <div class="actions">
          <router-link to="/roles/create" class="btn btn-primary">
            <i class="bi bi-plus-lg me-2"></i>
            Crear Rol
          </router-link>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card filters-card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label for="filterName" class="form-label">Nombre del Rol</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                id="filterName"
                v-model="filters.name"
                placeholder="Buscar por nombre..."
                @input="handleFilterChange"
              >
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="includeDeleted"
                v-model="filters.include_deleted"
                @change="handleFilterChange"
              >
              <label class="form-check-label" for="includeDeleted">
                Incluir eliminados
              </label>
            </div>
          </div>

          <div class="col-md-5">
            <div class="d-flex gap-2 justify-content-end">
              <button @click="clearFilters" class="btn btn-outline-secondary">
                <i class="bi bi-x-circle me-1"></i>
                Limpiar Filtros
              </button>
              <button @click="refreshRoles" class="btn btn-primary">
                <i class="bi bi-arrow-clockwise me-1"></i>
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Roles Table -->
    <div class="card">
      <div class="card-body">
        <!-- Error State -->
        <div v-if="roleStore.error && !roleStore.loading" class="alert alert-danger" role="alert">
          <h5 class="alert-heading">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Error al cargar los roles
          </h5>
          <p class="mb-2">{{ roleStore.error }}</p>
          <hr>
          <div class="d-flex gap-2">
            <button @click="refreshRoles" class="btn btn-sm btn-outline-danger">
              <i class="bi bi-arrow-clockwise me-1"></i>
              Reintentar
            </button>
            <button @click="roleStore.clearError" class="btn btn-sm btn-outline-secondary">
              <i class="bi bi-x-circle me-1"></i>
              Cerrar
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="roleStore.loading && roleStore.roles.length === 0" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando roles...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="roleStore.roles.length === 0 && !roleStore.error" class="text-center py-5">
          <i class="bi bi-shield-lock text-muted" style="font-size: 3rem;"></i>
          <h5 class="mt-3">No se encontraron roles</h5>
          <p class="text-muted">
            {{ hasActiveFilters ? 'Intenta cambiar los filtros de búsqueda.' : 'Comienza creando tu primer rol.' }}
          </p>
          <router-link to="/roles/create" class="btn btn-primary mt-2" v-if="!hasActiveFilters">
            <i class="bi bi-plus-lg me-2"></i>
            Crear Primer Rol
          </router-link>
        </div>

        <!-- Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Privilegios</th>
                <th>Creado</th>
                <th>Actualizado</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="role in roleStore.roles" :key="role.id">
                <td>{{ role.id }}</td>
                <td>
                  <strong>{{ role.name }}</strong>
                  <span v-if="role.is_deleted" class="badge bg-danger ms-2">
                    <i class="bi bi-trash me-1"></i>Eliminado
                  </span>
                </td>
                <td>
                  <div v-if="role.privileges && Object.keys(role.privileges).length > 0">
                    <span
                      v-for="(permissions, module) in role.privileges"
                      :key="module"
                      class="badge bg-secondary me-1 mb-1"
                      :title="Array.isArray(permissions) ? permissions.join(', ') : permissions"
                    >
                      {{ module }} ({{ Array.isArray(permissions) ? permissions.length : 0 }})
                    </span>
                  </div>
                  <span v-else class="text-muted">Sin privilegios</span>
                </td>
                <td>
                  <small>{{ formatDate(role.created_at) }}</small>
                </td>
                <td>
                  <small>{{ role.updated_at ? formatDate(role.updated_at) : '-' }}</small>
                </td>
                <td>
                  <span :class="getStatusBadgeClass(role)" class="badge">
                    {{ getStatusText(role) }}
                  </span>
                </td>
                <td>
                  <div class="btn-group btn-group-sm" role="group">
                    <router-link
                      :to="`/roles/${role.id}/edit`"
                      class="btn btn-outline-primary"
                      title="Editar"
                      v-if="!role.is_deleted"
                    >
                      <i class="bi bi-pencil"></i>
                    </router-link>

                    <button
                      @click="confirmRestore(role)"
                      class="btn btn-outline-success"
                      title="Restaurar"
                      v-if="role.is_deleted"
                    >
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>

                    <button
                      @click="confirmDelete(role)"
                      class="btn btn-outline-danger"
                      title="Eliminar"
                      v-if="!role.is_deleted"
                    >
                      <i class="bi bi-trash"></i>
                    </button>

                    <button
                      @click="confirmHardDelete(role)"
                      class="btn btn-outline-danger"
                      title="Eliminar permanentemente"
                      v-if="role.is_deleted"
                    >
                      <i class="bi bi-trash-fill"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="roleStore.pagination.pages > 1" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted">
            Mostrando {{ (roleStore.pagination.page - 1) * roleStore.pagination.size + 1 }}
            -
            {{ Math.min(roleStore.pagination.page * roleStore.pagination.size, roleStore.pagination.total) }}
            de {{ roleStore.pagination.total }} roles
          </div>

          <nav aria-label="Paginación">
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: roleStore.pagination.page === 1 }">
                <button class="page-link" @click="changePage(roleStore.pagination.page - 1)" :disabled="roleStore.pagination.page === 1">
                  <i class="bi bi-chevron-left"></i>
                </button>
              </li>

              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === roleStore.pagination.page }"
              >
                <button class="page-link" @click="changePage(page)">
                  {{ page }}
                </button>
              </li>

              <li class="page-item" :class="{ disabled: roleStore.pagination.page === roleStore.pagination.pages }">
                <button class="page-link" @click="changePage(roleStore.pagination.page + 1)" :disabled="roleStore.pagination.page === roleStore.pagination.pages">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    <!-- Modal de Confirmación Vue Nativo -->
    <Transition name="modal">
      <div v-if="confirmDialog.show" class="modal-overlay" @click="closeConfirmDialog">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="confirmDialog.icon" class="me-2"></i>
              {{ confirmDialog.title }}
            </h5>
            <button type="button" class="btn-close-custom" @click="closeConfirmDialog">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="modal-body">
            {{ confirmDialog.message }}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeConfirmDialog">
              Cancelar
            </button>
            <button
              type="button"
              :class="confirmDialog.buttonClass"
              class="btn"
              @click="executeConfirmAction"
            >
              {{ confirmDialog.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoleStore } from '../../stores/roles'

// Store
const roleStore = useRoleStore()

// Reactive state
const filters = ref({
  name: '',
  include_deleted: false
})

// Diálogo de confirmación
const confirmDialog = ref({
  show: false,
  title: '',
  message: '',
  icon: '',
  buttonClass: '',
  confirmText: '',
  action: null,
  roleId: null
})


// Computed
const hasActiveFilters = computed(() => {
  return filters.value.name !== '' || filters.value.include_deleted
})

const visiblePages = computed(() => {
  const current = roleStore.pagination.page
  const total = roleStore.pagination.pages
  const delta = 2
  const range = []

  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i)
  }

  if (current - delta > 2) {
    range.unshift('...')
  }
  if (current + delta < total - 1) {
    range.push('...')
  }

  range.unshift(1)
  if (total > 1) {
    range.push(total)
  }

  return range.filter((v, i, a) => a.indexOf(v) === i)
})

// Methods
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusBadgeClass = (role) => {
  if (role.is_deleted) {
    return 'bg-danger'
  }
  return 'bg-success'
}

const getStatusText = (role) => {
  if (role.is_deleted) {
    return 'Eliminado'
  }
  return 'Activo'
}

const handleFilterChange = () => {
  roleStore.setFilters(filters.value)
  roleStore.fetchRoles(1, roleStore.pagination.size)
}

const clearFilters = () => {
  filters.value = {
    name: '',
    include_deleted: false
  }
  roleStore.clearFilters()
  roleStore.fetchRoles(1, roleStore.pagination.size)
}

const refreshRoles = () => {
  roleStore.fetchRoles(roleStore.pagination.page, roleStore.pagination.size)
}

const changePage = (page) => {
  if (page >= 1 && page <= roleStore.pagination.pages) {
    roleStore.fetchRoles(page, roleStore.pagination.size)
  }
}

// Funciones del diálogo de confirmación
const openConfirmDialog = (config) => {
  confirmDialog.value = {
    show: true,
    ...config
  }
}

const closeConfirmDialog = () => {
  confirmDialog.value = {
    show: false,
    title: '',
    message: '',
    icon: '',
    buttonClass: '',
    confirmText: '',
    action: null,
    roleId: null
  }
}

const executeConfirmAction = async () => {
  if (confirmDialog.value.action && confirmDialog.value.roleId) {
    try {
      await confirmDialog.value.action(confirmDialog.value.roleId)
      closeConfirmDialog()
      await refreshRoles()
    } catch (error) {
      console.error('Error al ejecutar acción:', error)
    }
  }
}

// Funciones de confirmación específicas
const confirmDelete = (role) => {
  openConfirmDialog({
    title: 'Eliminar Rol',
    message: `¿Estás seguro de que deseas eliminar el rol "${role.name}"? Esta acción se puede revertir posteriormente.`,
    icon: 'bi bi-trash',
    buttonClass: 'btn-danger',
    confirmText: 'Eliminar',
    action: roleStore.deleteRole,
    roleId: role.id
  })
}

const confirmRestore = (role) => {
  openConfirmDialog({
    title: 'Restaurar Rol',
    message: `¿Deseas restaurar el rol "${role.name}"?`,
    icon: 'bi bi-arrow-counterclockwise',
    buttonClass: 'btn-success',
    confirmText: 'Restaurar',
    action: roleStore.restoreRole,
    roleId: role.id
  })
}

const confirmHardDelete = (role) => {
  openConfirmDialog({
    title: 'Eliminar Permanentemente',
    message: `¿Estás ABSOLUTAMENTE seguro de que deseas eliminar permanentemente el rol "${role.name}"? Esta acción NO se puede revertir.`,
    icon: 'bi bi-exclamation-triangle-fill',
    buttonClass: 'btn-danger',
    confirmText: 'Eliminar Permanentemente',
    action: roleStore.hardDeleteRole,
    roleId: role.id
  })
}

// Lifecycle
onMounted(async () => {
  try {
    // Load initial data
    await roleStore.fetchRoles()
  } catch (error) {
    console.error('Error initializing RoleList:', error)
  }
})

// Watch for filter changes in store
watch(() => roleStore.filters, (newFilters) => {
  filters.value = { ...newFilters }
}, { deep: true })
</script>

<style scoped>
.role-list {
  padding: 0;
}

.page-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.title-section p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary);
}

.stat-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.stat-details {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
}

.filters-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.table {
  margin-bottom: 0;
  color: var(--text-primary);
}

.table thead {
  background: var(--table-header-bg);
  color: var(--text-primary);
}

.table tbody tr:hover {
  background: var(--hover-bg);
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

.pagination {
  --bs-pagination-bg: var(--card-bg);
  --bs-pagination-border-color: var(--border-color);
  --bs-pagination-hover-bg: var(--hover-bg);
  --bs-pagination-active-bg: var(--bs-primary);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .stat-card {
    margin-bottom: 0.75rem;
  }
}

/* Modal de confirmación Vue nativo */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid var(--border-color);
  background: #f8f9fa;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
}

.btn-close-custom {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem 0.5rem;
  transition: color 0.2s;
}

.btn-close-custom:hover {
  color: #212529;
}

.modal-body {
  padding: 1.5rem;
  background: white;
  color: #212529;
  font-size: 0.95rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-color);
  background: #f8f9fa;
}

/* Transiciones del modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}

.modal-enter-to .modal-container,
.modal-leave-from .modal-container {
  transform: scale(1);
}
</style>
